import React, { useEffect, useState } from 'react';
import { Modal, Button, Select, Input, FormField, Text } from '@heartinternet/hi-ui';
import type { DomainOption } from '../types';
import { passwordIsStrong, USERNAME_PATTERN } from '../utils';

export interface AssignPayload {
  mailbox_id: string;
  login: string;
  domain: string;
  password: string;
  given_name: string;
  surname: string;
}

export interface AssignMailboxModalProps {
  isOpen: boolean;
  mailboxId: string;
  domains: DomainOption[];
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: AssignPayload) => void;
}

/**
 * Assign an unused allowance to a new mailbox address.
 * Mirrors the legacy assign-mailbox modal + manage.js validation, but the Assign button is
 * disabled until every required field is filled (portal convention) rather than silently
 * no-op'ing, and inline field errors replace the legacy weak-password / invalid-user boxes.
 */
export const AssignMailboxModal: React.FC<AssignMailboxModalProps> = ({
  isOpen,
  mailboxId,
  domains,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [domain, setDomain] = useState('');
  const [givenName, setGivenName] = useState('');
  const [surname, setSurname] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);

  // The parent keeps this modal permanently mounted (only toggling isOpen), so the field
  // state would otherwise persist between openings — a cancelled attempt's domain/name/
  // password/errors would reappear when Assign is opened for a different mailbox. Reset on
  // close, mirroring legacy manage.js which cleared the inputs each time the modal opened.
  /* eslint-disable react-hooks/set-state-in-effect -- reset the form when the modal closes */
  useEffect(() => {
    if (isOpen) return;
    setDomain('');
    setGivenName('');
    setSurname('');
    setLogin('');
    setPassword('');
    setPasswordError(undefined);
    setLoginError(undefined);
  }, [isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const canSubmit =
    domain !== '' &&
    givenName.trim() !== '' &&
    surname.trim() !== '' &&
    login !== '' &&
    password !== '';

  const handleAssign = () => {
    setPasswordError(undefined);
    setLoginError(undefined);
    if (!passwordIsStrong(password)) {
      setPasswordError('Your new password is too weak.');
      return;
    }
    if (!USERNAME_PATTERN.test(login)) {
      setLoginError('Only alphanumeric characters and .+_- are allowed.');
      return;
    }
    onSubmit({
      mailbox_id: mailboxId,
      login,
      domain,
      password,
      given_name: givenName.trim(),
      surname: surname.trim(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign mailbox"
      size="medium"
      footer={
        <div className="hi-ui-be-modal-actions">
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!canSubmit || submitting}
            loading={submitting}
          >
            Assign
          </Button>
        </div>
      }
    >
      <div className="hi-ui-be-modal-fields">
        <FormField label="Domain" required>
          <Select
            name="assign-domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            fullWidth
          >
            <option value="">Please select</option>
            {domains.map((d) => (
              <option key={d.domain} value={d.domain}>
                {d.domain}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Name" required>
          <Input
            name="given_name"
            value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            fullWidth
          />
        </FormField>
        <FormField label="Surname" required>
          <Input
            name="surname"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            fullWidth
          />
        </FormField>
        <FormField label="Mailbox user name" required error={loginError}>
          <Input name="login" value={login} onChange={(e) => setLogin(e.target.value)} fullWidth />
        </FormField>
        <FormField label="Password" required error={passwordError}>
          <Input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
          />
        </FormField>
      </div>
      <Text variant="caption">
        <span className="hi-ui-be-required-asterisk" aria-hidden="true">
          *
        </span>{' '}
        All fields are required
      </Text>
    </Modal>
  );
};
